import { Permission } from '@affine/graphql';

import type { WorkspaceMetadata } from '../metadata';

export const WORKSPACE_PERMISSION_CACHE_VERSION = 3;
export const WORKSPACE_PERMISSION_CACHE_TTL_MS = 48 * 60 * 60 * 1000;

export interface CachedWorkspacePermissionRole<Role = unknown> {
  accountId: string;
  cachedAt: number;
  isAdmin: boolean;
  isOwner: boolean;
  isTeam: boolean;
  role: Role;
  serverBaseUrl: string;
  serverId: string;
  version: typeof WORKSPACE_PERMISSION_CACHE_VERSION;
  workspaceId: string;
}

export interface CachedWorkspacePermissionCacheScope {
  accountId: string;
  serverBaseUrl: string;
  serverId: string;
  cachedAt: number;
  workspaces: WorkspaceMetadata[];
}

export interface WorkspacePermissionCacheScopeReader {
  get(key: string): unknown;
}

function isWorkspaceMetadata(value: unknown): value is WorkspaceMetadata {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const workspace = value as Record<string, unknown>;
  return (
    typeof workspace.id === 'string' &&
    workspace.id.length > 0 &&
    typeof workspace.flavour === 'string' &&
    workspace.flavour.length > 0 &&
    (workspace.initialized === undefined ||
      typeof workspace.initialized === 'boolean') &&
    (workspace.role === undefined ||
      Object.values(Permission).includes(workspace.role as Permission)) &&
    (workspace.team === undefined || typeof workspace.team === 'boolean') &&
    (workspace.deletionScheduledAt === undefined ||
      workspace.deletionScheduledAt === null ||
      typeof workspace.deletionScheduledAt === 'string')
  );
}

export function workspacePermissionCacheOwnerKey({
  accountId,
  serverBaseUrl,
  serverId,
}: {
  accountId: string;
  serverBaseUrl: string;
  serverId: string;
}): string {
  return `${serverId}:${serverBaseUrl}:${accountId}`;
}

export function workspacePermissionCacheScopeKey(serverId: string): string {
  return `workspace-permission-cache-scope:${serverId}`;
}

export function resolveWorkspaceLocalAuthorityAccountId({
  liveAccountId,
  cachedAccountId,
  scopeReader,
  serverBaseUrl,
  serverId,
  workspaceId,
  now = Date.now(),
}: {
  liveAccountId?: string | null;
  cachedAccountId?: string | null;
  scopeReader?: WorkspacePermissionCacheScopeReader | null;
  serverBaseUrl: string;
  serverId: string;
  workspaceId: string;
  now?: number;
}): string | null {
  if (liveAccountId) {
    return liveAccountId;
  }
  if (!cachedAccountId || !scopeReader) {
    return null;
  }

  const scope = scopeReader.get(workspacePermissionCacheScopeKey(serverId));
  if (
    !isCachedWorkspacePermissionCacheScopeFresh(
      scope,
      { serverBaseUrl, serverId },
      now
    )
  ) {
    return null;
  }
  if (
    scope.accountId !== cachedAccountId ||
    !scope.workspaces.some(({ id }) => id === workspaceId)
  ) {
    return null;
  }
  return cachedAccountId;
}

export function isWorkspacePermissionCacheFresh(
  updatedAt: number | null | undefined,
  now = Date.now()
): boolean {
  return (
    typeof updatedAt === 'number' &&
    Number.isFinite(updatedAt) &&
    updatedAt <= now &&
    now - updatedAt <= WORKSPACE_PERMISSION_CACHE_TTL_MS
  );
}

export function isCachedWorkspacePermissionCacheScopeFresh(
  scope: unknown,
  expected: { serverBaseUrl: string; serverId: string },
  now = Date.now()
): scope is CachedWorkspacePermissionCacheScope {
  return (
    typeof scope === 'object' &&
    scope !== null &&
    'serverId' in scope &&
    scope.serverId === expected.serverId &&
    'serverBaseUrl' in scope &&
    scope.serverBaseUrl === expected.serverBaseUrl &&
    'accountId' in scope &&
    typeof scope.accountId === 'string' &&
    scope.accountId.length > 0 &&
    'workspaces' in scope &&
    Array.isArray(scope.workspaces) &&
    scope.workspaces.every(isWorkspaceMetadata) &&
    'cachedAt' in scope &&
    typeof scope.cachedAt === 'number' &&
    isWorkspacePermissionCacheFresh(scope.cachedAt, now)
  );
}

export function isCachedWorkspacePermissionRoleFresh<Role>(
  role: CachedWorkspacePermissionRole<Role> | null | undefined,
  expected: {
    accountId: string;
    serverBaseUrl: string;
    serverId: string;
    workspaceId: string;
  },
  now = Date.now()
): role is CachedWorkspacePermissionRole<Role> {
  return (
    !!role &&
    role.version === WORKSPACE_PERMISSION_CACHE_VERSION &&
    role.accountId === expected.accountId &&
    role.serverId === expected.serverId &&
    role.serverBaseUrl === expected.serverBaseUrl &&
    role.workspaceId === expected.workspaceId &&
    typeof role.isAdmin === 'boolean' &&
    typeof role.isOwner === 'boolean' &&
    typeof role.isTeam === 'boolean' &&
    isWorkspacePermissionCacheFresh(role.cachedAt, now)
  );
}
